import { useCallback, useMemo } from 'react';
import { NarrativeMiniValuatorForm } from './NarrativeMiniValuatorForm';
import type { NarrativeForm } from '@/pages/Narrative/utils/schema';
import type { NarrativeMiniValuatorScreen } from '../utils';
import { NARRATIVE_MINI_VALUATOR_COMPANY_QUERY_PARAM } from '../utils';
import type { NarrativeValuatorCompany } from '@/pages/Narrative/hooks/narrativeValuatorCompanyFragment';
import { useTracking } from 'react-tracking';
import { transformFormValuesToValuation } from '@/pages/Narrative/utils/transformation';
import { usePathname } from '@/hooks/location/usePathname';
import { ROUTE_PUBLIC_NARRATIVE_EDIT } from '@/constants/routes';
import { getPath } from '@/router/utils';
import { useUpdatePublicNarrative } from '@/pages/Narrative/hooks/useUpdatePublicNarrative';
import { useNarrativeInvalidate } from '@/pages/Narrative/hooks/useNarrativeInvalidate';
import { usePastAnnualRevenueEarnings } from '@/pages/Narrative/hooks/usePastAnnualRevenueEarnings/usePastAnnualRevenueEarnings';
import type { FragmentOf } from 'gql.tada';
import { gql } from '@/utilities/gql';
import {
  CreateNarrativeFuturePEMultipleCalculator,
  ViewEditNarrativeFuturePEMultipleCalculator,
  useFuturePEMultipleCalculation,
  hasOnlyFairValue,
  FuturePEMultipleContext,
  calculateValuationInitialisation,
} from '../../Calculators';
import type { HeadingElements } from '@/utilities/heading';
import { VALUATION_FRAGMENT } from '@/pages/Narrative/hooks/valuationFragment';
export const NARRATIVE_MINI_VALUATOR_EDIT_NARRATIVE_FRAGMENT = gql(
  `
  fragment NarrativeMiniValuatorEditNarrativeFields on Narrative @_unmask {
    id
    title
    valuation {
      ...ValuationFields
    }
  },
`,
  [VALUATION_FRAGMENT]
);

type Props = {
  company: NarrativeValuatorCompany;
  setScreen: (screen: NarrativeMiniValuatorScreen) => void;
  narrative: FragmentOf<typeof NARRATIVE_MINI_VALUATOR_EDIT_NARRATIVE_FRAGMENT>;
  hideCompanySelect?: boolean;
  onSliderInteraction?: (isInteracting: boolean) => void;
  titleAs: HeadingElements;
};

export const NarrativeMiniValuatorFormEdit = ({
  company,
  setScreen,
  narrative,
  hideCompanySelect,
  onSliderInteraction,
  titleAs,
}: Props) => {
  const { trackEvent } = useTracking();
  const { invalidatePriority } = useNarrativeInvalidate();

  const { mutate: updateNarrative, isPending } = useUpdatePublicNarrative({
    onSuccess: () => {
      invalidatePriority(['narrative-mini-valuator']);
      trackEvent({
        action: 'save',
      });
    },
  });

  const handleSubmit = useCallback(
    (values: NarrativeForm) => {
      const { id } = company;
      const { title } = values;

      updateNarrative({
        input: {
          id: narrative.id,
          title,
          companyId: id,
          valuations: [
            transformFormValuesToValuation({
              values,
            }),
          ],
        },
      });
    },
    [updateNarrative, narrative, company]
  );

  const strategyContext = useMemo(
    () =>
      new FuturePEMultipleContext(
        hasOnlyFairValue(narrative.valuation)
          ? new CreateNarrativeFuturePEMultipleCalculator(company)
          : new ViewEditNarrativeFuturePEMultipleCalculator(narrative.valuation)
      ),
    [company, narrative.valuation]
  );

  const {
    calculateFairValue,
    calculateFairValueFromInitialisation,
    calculateRevenueGrowthRateFromFairValue,
  } = useFuturePEMultipleCalculation(strategyContext);

  const initialNarrativeForm: NarrativeForm = useMemo(() => {
    const calculatorOutputs = calculateValuationInitialisation({
      valuation: narrative.valuation,
      calculateFairValueFromInitialisation,
      calculateRevenueGrowthRateFromFairValue,
    });

    return {
      ...calculatorOutputs,
      // Not modified by form, updatedNarrative ignores this field
      userHoldsPosition: null,
      title: narrative.title ?? '',
      content: {
        // Not modified by form, updatedNarrative ignores this field
        text: '',
        html: '',
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [narrative.valuation, narrative.title]);

  const pathname = usePathname();

  const pastAnnualRevenueEarnings = usePastAnnualRevenueEarnings({
    company,
    fiscalData: narrative.valuation.fiscalData,
  });

  return (
    <NarrativeMiniValuatorForm
      company={company}
      pastAnnualRevenueEarnings={pastAnnualRevenueEarnings}
      initialValues={initialNarrativeForm}
      setScreen={setScreen}
      onSubmit={handleSubmit}
      isSubmitting={isPending}
      saveLabel="Save"
      enableReinitialize
      fullScreenPath={getPath({
        type: ROUTE_PUBLIC_NARRATIVE_EDIT,
        payload: { id: narrative.id },
        query: {
          company: company.id,
          returnPath: `${pathname}?${NARRATIVE_MINI_VALUATOR_COMPANY_QUERY_PARAM}=${company.canonicalURL}`,
        },
      })}
      calculateFairValue={calculateFairValue}
      calculateRevenueGrowthRateFromFairValue={
        calculateRevenueGrowthRateFromFairValue
      }
      hideCompanySelect={hideCompanySelect}
      onSliderInteraction={onSliderInteraction}
      titleAs={titleAs}
    />
  );
};
